import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLmsEmployees } from "../../redux/erpSlice"; // Changed import
import { fetchAssignedLeavesByEmail, assignLeave, autoAssignAllLeaveLimits } from "../../redux/assignLeaveSlice";
import { fetchLeaveLimits } from "../../redux/leaveLimitSlice";

const AssignLeaveModal = ({
  isModalOpen,
  isEdit,
  onSubmit,
  handleSubmit,
  setIsModalOpen,
  register,
  reset,
  watch,
}) => {
  const dispatch = useDispatch();
  const { lmsEmployees, loading, error } = useSelector((state) => state.lmsEmployees); // Changed selector
  const { assignedLeaves } = useSelector((state) => state.assignLeave);
  const { leaveLimits } = useSelector((state) => state.leaveLimits);
  
  const [selectedTeacherEmail, setSelectedTeacherEmail] = useState("");
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [isAutoAssigning, setIsAutoAssigning] = useState(false);
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState([]);
  const [isSelectiveAssigning, setIsSelectiveAssigning] = useState(false);

  const watchedTeacherEmail = watch("teacherEmail");

  useEffect(() => {
    // Fetch LMS employees and leave limits when the modal opens
    if (isModalOpen) {
      dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: 100 })); // Changed to fetchLmsEmployees
      dispatch(fetchLeaveLimits());
    }
  }, [isModalOpen, dispatch]);

  useEffect(() => {
    if (watchedTeacherEmail && watchedTeacherEmail !== selectedTeacherEmail) {
      setSelectedTeacherEmail(watchedTeacherEmail);
      // Fetch existing assignments for the selected teacher
      dispatch(fetchAssignedLeavesByEmail(watchedTeacherEmail));
    }
  }, [watchedTeacherEmail, dispatch, selectedTeacherEmail]);

  useEffect(() => {
    if (selectedTeacherEmail && assignedLeaves.length > 0) {
      const teacherAssignments = assignedLeaves.filter(
        assignment => assignment.teacherEmail === selectedTeacherEmail
      );
      setExistingAssignments(teacherAssignments);
    } else {
      setExistingAssignments([]);
    }
  }, [assignedLeaves, selectedTeacherEmail]);

  // Auto-assign all leave limits to the selected teacher
  const handleAutoAssign = async () => {
    if (!selectedTeacherEmail) {
      alert("Please select a teacher first");
      return;
    }

    if (existingAssignments.length > 0) {
      alert("This teacher already has leave assignments. Please use the update function to modify existing assignments.");
      return;
    }

    setIsAutoAssigning(true);
    try {
      // Use the new auto-assign API endpoint
      const result = await dispatch(autoAssignAllLeaveLimits(selectedTeacherEmail)).unwrap();
      
      alert(result.message || `Successfully auto-assigned leave limits to ${selectedTeacherEmail}`);
      
      // Refresh the assigned leaves for this teacher
      dispatch(fetchAssignedLeavesByEmail(selectedTeacherEmail));
      
    } catch (error) {
      alert(`Error auto-assigning leaves: ${error}`);
    } finally {
      setIsAutoAssigning(false);
    }
  };

  // Handle selective leave type assignment
  const handleSelectiveAssign = async () => {
    if (!selectedTeacherEmail) {
      alert("Please select a teacher first");
      return;
    }

    if (selectedLeaveTypes.length === 0) {
      alert("Please select at least one leave type to assign");
      return;
    }

    if (existingAssignments.length > 0) {
      alert("This teacher already has leave assignments. Please use the update function to modify existing assignments.");
      return;
    }

    setIsSelectiveAssigning(true);
    try {
      // Assign selected leave types one by one
      const assignments = [];
      for (const leaveType of selectedLeaveTypes) {
        const leaveLimit = leaveLimits.find(limit => limit.leaveType === leaveType);
        if (leaveLimit) {
          const assignment = await dispatch(assignLeave({
            teacherEmail: selectedTeacherEmail,
            leaveType: leaveType,
            numberOfLeave: leaveLimit.maxDays
          })).unwrap();
          assignments.push(assignment);
        }
      }
      
      alert(`Successfully assigned ${assignments.length} leave types to ${selectedTeacherEmail}`);
      
      // Refresh the assigned leaves for this teacher
      dispatch(fetchAssignedLeavesByEmail(selectedTeacherEmail));
      
    } catch (error) {
      alert(`Error assigning selected leaves: ${error}`);
    } finally {
      setIsSelectiveAssigning(false);
    }
  };

  // Handle checkbox changes
  const handleLeaveTypeToggle = (leaveType) => {
    setSelectedLeaveTypes(prev => {
      if (prev.includes(leaveType)) {
        return prev.filter(type => type !== leaveType);
      } else {
        return [...prev, leaveType];
      }
    });
  };

  // Handle select all/none
  const handleSelectAll = () => {
    const availableLeaveTypes = leaveLimits.map(limit => limit.leaveType);
    setSelectedLeaveTypes(availableLeaveTypes);
  };

  const handleSelectNone = () => {
    setSelectedLeaveTypes([]);
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-11/12 md:w-2/3 lg:w-1/3 overflow-hidden rounded shadow-lg relative">
        <div className="flex justify-between items-center border-b pb-2 px-4">
          <h2 className="text-xl font-semibold">Assign Leave Types</h2>
          <button
            className="p-2 hover:bg-gray-200 rounded-full"
            onClick={() => {
              setIsModalOpen(false);
              reset();
            }}
          >
            <X />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4">
          <div>
            {/* Select Employee by Email */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">
                Select Employee
              </label>
              <select
                {...register("teacherEmail", { required: true })}
                className="mt-2 w-full border rounded p-2"
              >
                <option value="">Select Employee</option>
                {loading ? (
                  <option>Loading...</option>
                ) : error ? (
                  <option>Error loading employees</option>
                ) : lmsEmployees.length === 0 ? (
                  <option>No employees available</option>
                ) : (
                  lmsEmployees.map((employee) => (
                    <option
                      key={employee.id}
                      value={employee.email}
                    >
                      {`${employee.firstName} ${employee.lastName} (${employee.email})`}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Show existing assignments */}
            {existingAssignments.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <h4 className="font-medium text-yellow-800 mb-2">Current Leave Assignments:</h4>
                {existingAssignments.map((assignment, index) => (
                  <div key={index} className="text-sm text-yellow-700">
                    • {assignment.leaveType.replace(/([A-Z])/g, " $1").trim()}: {assignment.numberOfLeave} leaves
                  </div>
                ))}
                <p className="text-xs text-yellow-600 mt-2">
                  Note: You cannot assign the same leave type twice. Use the update function to modify existing assignments.
                </p>
              </div>
            )}

            {/* Leave Type Selection */}
            {existingAssignments.length === 0 && selectedTeacherEmail && leaveLimits.length > 0 && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                <h4 className="font-medium text-green-800 mb-2">Select Leave Types to Assign</h4>
                <p className="text-sm text-green-700 mb-3">
                  Choose which leave types you want to assign to this employee.
                </p>
                
                {/* Select All/None Buttons */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={handleSelectNone}
                    className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700"
                  >
                    Select None
                  </button>
                </div>

                {/* Leave Type Checkboxes */}
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {leaveLimits.map((limit) => (
                    <div key={limit.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`leave-${limit.id}`}
                        checked={selectedLeaveTypes.includes(limit.leaveType)}
                        onChange={() => handleLeaveTypeToggle(limit.leaveType)}
                        className="mr-2"
                      />
                      <label htmlFor={`leave-${limit.id}`} className="text-sm text-gray-700 flex-1">
                        <span className="font-medium">{limit.leaveType}</span>
                        <span className="text-gray-500 ml-2">
                          ({limit.maxDays} days, {limit.isPaid ? 'paid' : 'unpaid'})
                        </span>
                        {limit.autoAssign && (
                          <span className="text-green-600 text-xs ml-2">✅ Auto-Assign</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>

                {/* Assign Selected Button */}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleSelectiveAssign}
                    disabled={isSelectiveAssigning || selectedLeaveTypes.length === 0}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSelectiveAssigning ? "Assigning..." : `Assign Selected (${selectedLeaveTypes.length})`}
                  </button>
                </div>
              </div>
            )}

            {/* Auto-Assign Button */}
            {existingAssignments.length === 0 && selectedTeacherEmail && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-medium text-blue-800 mb-2">Quick Assign All Leave Types</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Automatically assign all configured leave limits to this teacher.
                </p>
                <button
                  type="button"
                  onClick={handleAutoAssign}
                  disabled={isAutoAssigning}
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  {isAutoAssigning ? "Assigning..." : "Auto-Assign All Leave Types"}
                </button>
              </div>
            )}



            {/* Actions */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
                onClick={() => {
                  setIsModalOpen(false);
                  reset();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignLeaveModal;